// A form's field messages: its own rules (`problems`, { field: message },
// recomputed every render) shown for a field once it was edited or a save was
// tried, plus the server's refusals placed beside their fields. Editing a
// field clears the server's word on it.
//
//   const checks = useFieldErrors(problems);
//   <Input onChange={(e) => { checks.touch('name'); … }} {...checks.describe('name', 'hook')} />
//   <FieldError id="hook-name-error" message={checks.errors.name} />
//   const submit = async () => { if (!checks.attempt()) return; const res = await …;
//                                if (!res.success) checks.showServer(res, FIELDS); };

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { splitErrors } from '../utils/fieldErrors';

const within = (key, field) => key === field || key.startsWith(`${field}.`);

export function useFieldErrors(problems) {
  const [touched, setTouched] = useState({});
  const [tried, setTried] = useState(false);
  const [server, setServer] = useState({});
  const failing = Object.keys(problems).length > 0;

  const errors = useMemo(() => {
    const visible = {};
    Object.entries(problems).forEach(([key, message]) => {
      if (tried || touched[key] || touched[key.split('.')[0]]) visible[key] = message;
    });
    return { ...visible, ...server };
  }, [problems, touched, tried, server]);

  const touch = useCallback((field) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    setServer((prev) => {
      const keys = Object.keys(prev).filter((k) => within(k, field) || within(field, k));
      if (!keys.length) return prev;
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  }, []);

  // A save was tried: every rule shows now; true when there is none.
  const attempt = () => {
    setTried(true);
    return !failing;
  };

  const showServer = (res, fields, codeFields, overrides) => {
    const { errors: placed, message } = splitErrors(res, fields, codeFields, overrides);
    setServer(placed);
    if (message) toast.error(message);
  };

  const reset = useCallback(() => {
    setTouched({});
    setTried(false);
    setServer({});
  }, []);

  const describe = (field, prefix) => (errors[field]
    ? { 'aria-invalid': true, 'aria-describedby': `${prefix}-${field}-error` }
    : {});

  return { errors, touch, attempt, showServer, reset, describe, blocked: tried && failing };
}

export default useFieldErrors;
